import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { Sparkles, LogOut, Menu, X, ChevronDown, ChevronUp, ChevronRight, Users, Compass, BookOpen, Calculator, Heart, LogIn, UserPlus } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import NotificationBell from "@/components/NotificationBell";
import MyIepsNavItem from "@/components/MyIepsNavItem";
import { prefetchBlog } from "@/lib/blogPrefetch";

export default function Navbar() {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const [resOpen, setResOpen] = useState(false);
  const [condOpen, setCondOpen] = useState(false);
  // iter180.6 - Mobile menu accordions. All sections start CLOSED so the
  // user controls what to open (Kiddo+ pattern). Uniform across all pages
  // because Navbar is rendered globally.
  const [mobileFeaturesOpen, setMobileFeaturesOpen] = useState(false);
  const [mobileResourcesOpen, setMobileResourcesOpen] = useState(false);
  const [mobileToolsOpen, setMobileToolsOpen] = useState(false);
  const resRef = useRef(null);
  const condRef = useRef(null);

  const handleLogout = () => { logout(); nav("/"); };

  // Body scroll lock + close on Esc + close on route change.
  // iOS Safari ignores `overflow:hidden` on body, so we use the classic
  // position-fixed-with-preserved-scroll trick that works across all browsers.
  // Without this, scrolling inside the menu's content propagates to the page
  // beneath once the menu reaches its scroll boundary.
  useEffect(() => {
    if (!open) return undefined;
    const scrollY = window.scrollY || window.pageYOffset || 0;
    const body = document.body;
    const html = document.documentElement;
    const prev = {
      bodyOverflow: body.style.overflow,
      bodyPosition: body.style.position,
      bodyTop: body.style.top,
      bodyWidth: body.style.width,
      htmlOverflow: html.style.overflow,
    };
    body.style.overflow = "hidden";
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.left = "0";
    body.style.right = "0";
    body.style.width = "100%";
    html.style.overflow = "hidden";

    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => {
      body.style.overflow = prev.bodyOverflow;
      body.style.position = prev.bodyPosition;
      body.style.top = prev.bodyTop;
      body.style.left = "";
      body.style.right = "";
      body.style.width = prev.bodyWidth;
      html.style.overflow = prev.htmlOverflow;
      // Restore the original scroll position (the body was fixed at -scrollY,
      // so we need to scroll back to where the user was).
      window.scrollTo(0, scrollY);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  useEffect(() => { setOpen(false); }, [loc.pathname]);

  const dashHref = user
    ? user.role === "admin" ? "/admin/dashboard"
      : user.role === "educator" ? "/educator"
      : user.role === "therapist" ? "/therapist/dashboard"
      : "/parent"
    : "/login";

  // 5 top-level links. Conditions and Resources are dropdowns. The "Resources"
  // dropdown groups Free tools + Library + Marketplace + reference pages so
  // users have a single mental model for "stuff you can use" (iter75 IA cleanup).
  const primary = [
    { to: "/try-ai-studio", label: t("nav.ai_studio", "SpectraMind Studio") },
    { to: "/for-schools", label: t("nav.for_schools", "For schools") },
    { to: "/pricing", label: t("nav.pricing", "Pricing") },
    { to: "/marketplace", label: t("nav.sessions", "Sessions") },
  ];

  // iter180 - "Marketplace" (`/library`) removed from Resources dropdown
  // per user request. Sessions marketplace remains in the top nav.
  const resourcesGroup = [
    { to: "/free-tools", label: "Interactive tools",     desc: "27+ in-browser makers, builders & simulations" },
    { to: "/free-resources",  label: "Free library",          desc: "Printables, social stories, ebooks & games" },
    { to: "/science",    label: "Science",               desc: "12 cited studies + framework table" },
    { to: "/methodology",label: "How the SpectraMind Framework works", desc: "Methodology + clinical audit" },
    { to: "/quality-gate", label: "How the Quality Gate works", desc: "30 clinical checks, named frameworks" },
    { to: "/blog",       label: "Blog",                  desc: "Practitioner notes + research" },
    { to: "/faq",        label: "FAQ",                   desc: "Common questions" },
  ];

  const conditionsGroup = [
    { to: "/conditions/autism", label: "Autism", desc: "Carol Gray Social Stories + TEACCH" },
    { to: "/conditions/adhd", label: "ADHD", desc: "Barkley EF + AAP 2019 + NICE NG87" },
    { to: "/conditions/dyslexia", label: "Dyslexia", desc: "Structured literacy + OpenDyslexic" },
    { to: "/conditions/down-syndrome", label: "Down Syndrome", desc: "Sight-word + visual scaffolds" },
    { to: "/conditions/learning-disabilities", label: "Learning Disabilities", desc: "IDEA SLD + UDL 3.0" },
    { to: "/conditions/slow-learner", label: "Slow Learner / BIF", desc: "Shaw 2010 + UDL 3.0" },
    { to: "/conditions/nf1", label: "Neurofibromatosis (NF1)", desc: "Combination profile + OHI IDEA" },
    { to: "/conditions/dysgraphia", label: "Dysgraphia", desc: "Sentence frames + keyboarding-first" },
    { to: "/conditions/dyscalculia", label: "Dyscalculia", desc: "CRA sequence + zero timed tasks" },
    { to: "/conditions/dyspraxia", label: "Dyspraxia / DCD", desc: "Motor-planning scaffolds + PE alternatives" },
  ];

  // Close dropdown on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (resRef.current && !resRef.current.contains(e.target)) setResOpen(false);
      if (condRef.current && !condRef.current.contains(e.target)) setCondOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);
  // Close on route change
  useEffect(() => { setResOpen(false); setCondOpen(false); setOpen(false); }, [loc.pathname]);

  const onResourcesPath = resourcesGroup.some((r) => r.to === loc.pathname);
  const onConditionsPath = conditionsGroup.some((c) => c.to === loc.pathname);

  // iter210.5 - Therapist role gets a stripped-down navbar per FERPA
  // scope: Dashboard + Account only. No Studio, no Marketplace, no
  // Resources / Conditions dropdowns, no Inbox. Therapists must not
  // discover content-generation surfaces or cross-caseload links.
  if (user && user.role === "therapist") {
    return (
      <header className="glass nav-sticky z-[100] border-b border-[#2D312E]/5" data-testid="main-navbar-therapist">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="h-16 flex items-center justify-between gap-4">
            <Link to="/therapist/dashboard" className="flex items-center gap-2.5 flex-shrink-0"
                  data-testid="nav-logo-link" aria-label="SpectraLearn home">
              <img src="/brand/spectralearn-mark-80.png" alt="" aria-hidden="true"
                   className="h-9 w-9 rounded-2xl" width={36} height={36} />
              <span className="font-display text-lg font-bold tracking-tight">SpectraLearn</span>
            </Link>
            <div className="flex items-center gap-2">
              <Link to="/therapist/dashboard" data-testid="nav-therapist-dashboard-link"
                    className="px-4 py-2.5 rounded-xl bg-[#4F7660] text-white font-medium hover:bg-[#3B5A48] text-[15px]">
                Dashboard
              </Link>
              <Link to="/account" data-testid="nav-therapist-account-link"
                    className="px-3 py-2 rounded-xl text-[#5C635E] hover:bg-[#F4F1EA] text-[15px]">
                Account
              </Link>
              <button onClick={handleLogout} data-testid="nav-logout-btn"
                      className="p-2.5 rounded-xl border border-[#2D312E]/10 text-[#5C635E] hover:bg-[#F4F1EA]"
                      aria-label="Logout">
                <LogOut className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </header>
    );
  }

  return (
    <header className="glass nav-sticky z-[100] border-b border-[#2D312E]/5" data-testid="main-navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="h-16 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2.5 flex-shrink-0" data-testid="nav-logo-link" aria-label="SpectraLearn home">
            <img
              src="/brand/spectralearn-mark-80.png"
              alt=""
              aria-hidden="true"
              className="h-9 w-9 rounded-2xl"
              width={36}
              height={36}
            />
            <span className="font-display text-lg font-bold tracking-tight">SpectraLearn</span>
          </Link>

          <nav className="hidden lg:flex items-center gap-1 flex-1 justify-center">
            {primary.map((l) => (
              <Link key={l.to} to={l.to} data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}-link`}
                className={`px-4 py-2 rounded-xl text-[15px] transition-colors whitespace-nowrap ${
                  loc.pathname === l.to ? "bg-[#E8F0EC] text-[#2D312E]" : "text-[#5C635E] hover:bg-[#F4F1EA]"
                }`}>
                {l.label}
              </Link>
            ))}
            <div className="relative" ref={resRef}>
              <button
                onClick={() => { setResOpen((o) => !o); setCondOpen(false); }}
                onMouseEnter={prefetchBlog}
                onFocus={prefetchBlog}
                data-testid="nav-resources-toggle"
                className={`px-4 py-2 rounded-xl text-[15px] transition-colors inline-flex items-center gap-1 whitespace-nowrap ${
                  onResourcesPath || resOpen ? "bg-[#E8F0EC] text-[#2D312E]" : "text-[#5C635E] hover:bg-[#F4F1EA]"
                }`}>
                Resources <ChevronDown className={`h-4 w-4 transition-transform ${resOpen ? "rotate-180" : ""}`} />
              </button>
              {resOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-[320px] bg-white rounded-2xl shadow-xl border border-[#2D312E]/5 p-2 z-50" data-testid="nav-resources-dropdown">
                  {resourcesGroup.map((r) => (
                    <Link
                      key={r.to}
                      to={r.to}
                      data-testid={`nav-resources-${r.label.toLowerCase().replace(/\s+/g, "-")}-link`}
                      onMouseEnter={r.to === "/blog" ? prefetchBlog : undefined}
                      onFocus={r.to === "/blog" ? prefetchBlog : undefined}
                      className="block px-3 py-2.5 rounded-xl hover:bg-[#F4F1EA] transition-colors">
                      <div className="text-[15px] font-medium text-[#2D312E]">{r.label}</div>
                      <div className="text-xs text-[#7A827C] mt-0.5">{r.desc}</div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
            <div className="relative" ref={condRef}>
              <button onClick={() => { setCondOpen((o) => !o); setResOpen(false); }} data-testid="nav-conditions-toggle"
                className={`px-4 py-2 rounded-xl text-[15px] transition-colors inline-flex items-center gap-1 whitespace-nowrap ${
                  onConditionsPath || condOpen ? "bg-[#E8F0EC] text-[#2D312E]" : "text-[#5C635E] hover:bg-[#F4F1EA]"
                }`}>
                Conditions <ChevronDown className={`h-4 w-4 transition-transform ${condOpen ? "rotate-180" : ""}`} />
              </button>
              {condOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-[340px] bg-white rounded-2xl shadow-xl border border-[#2D312E]/5 p-2 z-50" data-testid="nav-conditions-dropdown">
                  {conditionsGroup.map((c) => (
                    <Link key={c.to} to={c.to} data-testid={`nav-conditions-${c.label.toLowerCase().replace(/\s+/g, "-")}-link`}
                      className="block px-3 py-2.5 rounded-xl hover:bg-[#F4F1EA] transition-colors">
                      <div className="text-[15px] font-medium text-[#2D312E]">{c.label}</div>
                      <div className="text-xs text-[#7A827C] mt-0.5">{c.desc}</div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </nav>

          <div className="hidden lg:flex items-center gap-2 flex-shrink-0">
            {user ? (
              <>
                <MyIepsNavItem />
                <NotificationBell />
                <Link to="/messages" data-testid="nav-messages-link"
                  className="px-3 py-2 rounded-xl text-[#5C635E] hover:bg-[#F4F1EA] text-[15px]">Inbox</Link>
                <Link to={dashHref} data-testid="nav-dashboard-link"
                  className="px-4 py-2.5 rounded-xl bg-[#4F7660] text-white font-medium hover:bg-[#3B5A48] text-[15px]">
                  Dashboard
                </Link>
                <button onClick={handleLogout} data-testid="nav-logout-btn"
                  className="p-2.5 rounded-xl border border-[#2D312E]/10 text-[#5C635E] hover:bg-[#F4F1EA]" aria-label="Logout">
                  <LogOut className="h-4 w-4" />
                </button>
              </>
            ) : (
              <>
                <Link to="/login" data-testid="nav-login-link"
                  className="px-4 py-2.5 rounded-xl text-[#2D312E] hover:bg-[#F4F1EA] font-medium text-[15px]">
                  {t("nav.signin", "Sign in")}
                </Link>
                <Link to="/register" data-testid="nav-register-link"
                  className="px-4 py-2.5 rounded-xl bg-[#E07A5F] text-white font-medium hover:bg-[#C9664D] text-[15px] whitespace-nowrap">
                  {t("nav.getstarted", "Get started")}
                </Link>
              </>
            )}
          </div>

          <button onClick={() => setOpen(!open)} className="lg:hidden p-2 rounded-xl border border-[#2D312E]/10"
            aria-label="Toggle menu" data-testid="nav-mobile-toggle" aria-expanded={open}>
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>

        {/* Mobile drawer is portalled to <body> so the header's backdrop-filter
            stacking context (created by `.glass`) does NOT trap it underneath
            sibling elements that have their own stacking context. */}
        {/* Mobile drawer is portalled to <body> so the header's backdrop-filter
            stacking context (created by `.glass`) does NOT trap it underneath
            sibling elements that have their own stacking context.
            iter180.6 - Redesigned to match the Kiddo+ reference layout:
            right-anchored 85%-width drawer, cream canvas, all accordions
            collapsed by default, Developmental Reports removed from Features. */}
        {open && typeof document !== "undefined" && createPortal(
          <>
            {/* Body scroll lock + click-outside backdrop */}
            <div
              className="lg:hidden fixed inset-0"
              style={{ zIndex: 190, background: "rgba(15,24,48,0.35)", backdropFilter: "blur(4px)" }}
              onClick={() => setOpen(false)}
              aria-hidden="true"
            />
            <aside
              className="lg:hidden fixed top-0 right-0 h-full w-[85%] max-w-[420px] flex flex-col shadow-2xl"
              style={{
                zIndex: 200,
                background: "#FDF6EC",
                WebkitOverflowScrolling: "touch",
              }}
              data-testid="nav-mobile-menu"
              role="dialog"
              aria-label="Main menu"
            >
              {/* Header row - logo + close */}
              <div className="flex items-center justify-between px-5 pt-5 pb-3 shrink-0">
                <Link to="/" onClick={() => setOpen(false)} className="flex items-center gap-2.5" data-testid="nav-mobile-logo">
                  <img src="/brand/spectralearn-mark-80.png" alt="" aria-hidden="true" className="h-10 w-10 rounded-2xl" />
                  <span className="text-xl font-black tracking-tight" style={{ color: "#1B1F3B", fontFamily: "'DM Sans','Inter',sans-serif" }}>
                    SpectraLearn
                  </span>
                </Link>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="h-9 w-9 rounded-full flex items-center justify-center border bg-white"
                  style={{ borderColor: "#F2D9C6" }}
                  aria-label="Close menu"
                  data-testid="nav-mobile-close"
                >
                  <X className="h-4 w-4" style={{ color: "#5C635E" }} />
                </button>
              </div>

              {/* Divider */}
              <div className="mx-5 border-t shrink-0" style={{ borderColor: "#F2D9C6" }} />

              {/* Scrollable body */}
              <div className="flex-1 overflow-y-auto overscroll-contain px-4 py-4 space-y-2">
                {/* Features (accordion, closed by default) */}
                <div className="rounded-3xl overflow-hidden transition-colors" style={{ background: mobileFeaturesOpen ? "#FFE7D6" : "transparent" }}>
                  <button
                    type="button"
                    onClick={() => setMobileFeaturesOpen((v) => !v)}
                    data-testid="nav-mobile-features-toggle"
                    aria-expanded={mobileFeaturesOpen}
                    className="w-full flex items-center gap-3 px-5 py-4 text-left"
                  >
                    <Sparkles className="h-5 w-5" style={{ color: mobileFeaturesOpen ? "#F0805C" : "#1B1F3B" }} />
                    <span className="flex-1 text-[17px] font-bold" style={{ color: mobileFeaturesOpen ? "#E5533E" : "#1B1F3B" }}>Features</span>
                    {mobileFeaturesOpen
                      ? <ChevronUp   className="h-5 w-5" style={{ color: "#E5533E" }} />
                      : <ChevronDown className="h-5 w-5" style={{ color: "#5C635E" }} />}
                  </button>
                  {mobileFeaturesOpen && (
                    <div className="pb-4 pl-8 pr-4">
                      <div className="relative">
                        <div aria-hidden="true" className="absolute left-0 top-1 bottom-1 w-px" style={{ background: "#F2D9C6" }} />
                        <ul className="space-y-1">
                          {[
                            { to: "/conditions",  label: "Who We Support",   icon: Users,    testid: "nav-mobile-who" },
                            { to: "/about",       label: "Why SpectraLearn", icon: Sparkles, testid: "nav-mobile-why" },
                            { to: "/methodology", label: "How It Works",     icon: Compass,  testid: "nav-mobile-how" },
                          ].map((f) => {
                            const Icon = f.icon;
                            return (
                              <li key={f.label}>
                                <Link
                                  to={f.to}
                                  onClick={() => setOpen(false)}
                                  data-testid={f.testid}
                                  className="flex items-center gap-3 py-3 pl-4 pr-2 rounded-xl hover:bg-white/50"
                                  style={{ color: "#1B1F3B" }}
                                >
                                  <Icon className="h-5 w-5" style={{ color: "#1B1F3B" }} strokeWidth={1.8} />
                                  <span className="flex-1 text-[15px] font-medium">{f.label}</span>
                                  <ChevronRight className="h-4 w-4" style={{ color: "#94A3B8" }} />
                                </Link>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    </div>
                  )}
                </div>

                {/* Pricing (single row) */}
                <Link
                  to="/pricing"
                  onClick={() => setOpen(false)}
                  data-testid="nav-mobile-pricing"
                  className="flex items-center gap-3 px-5 py-4"
                  style={{ color: "#1B1F3B" }}
                >
                  <Heart className="h-5 w-5" strokeWidth={1.8} />
                  <span className="flex-1 text-[17px] font-bold">Pricing</span>
                  <ChevronRight className="h-5 w-5" style={{ color: "#94A3B8" }} />
                </Link>

                {/* Resources (accordion) */}
                <div className="rounded-3xl overflow-hidden" style={{ background: mobileResourcesOpen ? "#FFE7D6" : "transparent" }}>
                  <button
                    type="button"
                    onClick={() => { setMobileResourcesOpen((v) => !v); prefetchBlog(); }}
                    data-testid="nav-mobile-resources-toggle"
                    aria-expanded={mobileResourcesOpen}
                    className="w-full flex items-center gap-3 px-5 py-4 text-left"
                  >
                    <BookOpen className="h-5 w-5" style={{ color: mobileResourcesOpen ? "#F0805C" : "#1B1F3B" }} strokeWidth={1.8} />
                    <span className="flex-1 text-[17px] font-bold" style={{ color: mobileResourcesOpen ? "#E5533E" : "#1B1F3B" }}>Resources</span>
                    {mobileResourcesOpen
                      ? <ChevronUp   className="h-5 w-5" style={{ color: "#E5533E" }} />
                      : <ChevronDown className="h-5 w-5" style={{ color: "#5C635E" }} />}
                  </button>
                  {mobileResourcesOpen && (
                    <div className="pb-4 pl-8 pr-4">
                      <div className="relative">
                        <div aria-hidden="true" className="absolute left-0 top-1 bottom-1 w-px" style={{ background: "#F2D9C6" }} />
                        <ul className="space-y-1">
                          {resourcesGroup.map((r) => (
                            <li key={r.label}>
                              <Link
                                to={r.to}
                                onClick={() => setOpen(false)}
                                onTouchStart={r.to === "/blog" ? prefetchBlog : undefined}
                                className="flex items-center gap-3 py-2.5 pl-4 pr-2 rounded-xl hover:bg-white/50"
                                style={{ color: "#1B1F3B" }}
                              >
                                <span className="flex-1 text-[14px] font-medium">{r.label}</span>
                                <ChevronRight className="h-4 w-4" style={{ color: "#94A3B8" }} />
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}
                </div>

                {/* Free Tools (accordion with NEW badge) */}
                <div className="rounded-3xl overflow-hidden" style={{ background: mobileToolsOpen ? "#FFE7D6" : "transparent" }}>
                  <button
                    type="button"
                    onClick={() => setMobileToolsOpen((v) => !v)}
                    data-testid="nav-mobile-tools-toggle"
                    aria-expanded={mobileToolsOpen}
                    className="w-full flex items-center gap-3 px-5 py-4 text-left"
                  >
                    <Calculator className="h-5 w-5" style={{ color: mobileToolsOpen ? "#F0805C" : "#1B1F3B" }} strokeWidth={1.8} />
                    <span className="text-[17px] font-bold" style={{ color: mobileToolsOpen ? "#E5533E" : "#1B1F3B" }}>Free Tools</span>
                    <span className="ml-1 px-2 py-0.5 rounded-full text-[10px] font-black tracking-wider text-white" style={{ background: "#F0805C" }} aria-label="new">NEW</span>
                    <span className="flex-1" />
                    {mobileToolsOpen
                      ? <ChevronUp   className="h-5 w-5" style={{ color: "#E5533E" }} />
                      : <ChevronDown className="h-5 w-5" style={{ color: "#5C635E" }} />}
                  </button>
                  {mobileToolsOpen && (
                    <div className="pb-4 pl-8 pr-4">
                      <div className="relative">
                        <div aria-hidden="true" className="absolute left-0 top-1 bottom-1 w-px" style={{ background: "#F2D9C6" }} />
                        <ul className="space-y-1">
                          {[
                            { to: "/free-tools",     label: "Free tools & resources" },
                            { to: "/try-ai-studio",  label: "Try SpectraMind Studio" },
                            { to: "/courses",        label: "Practitioner courses" },
                          ].map((r) => (
                            <li key={r.label}>
                              <Link
                                to={r.to}
                                onClick={() => setOpen(false)}
                                className="flex items-center gap-3 py-2.5 pl-4 pr-2 rounded-xl hover:bg-white/50"
                                style={{ color: "#1B1F3B" }}
                              >
                                <span className="flex-1 text-[14px] font-medium">{r.label}</span>
                                <ChevronRight className="h-4 w-4" style={{ color: "#94A3B8" }} />
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}
                </div>

                {/* Conditions - top-level link to /conditions index */}
                <Link
                  to="/conditions"
                  onClick={() => setOpen(false)}
                  data-testid="nav-mobile-conditions"
                  className="flex items-center gap-3 px-5 py-4"
                  style={{ color: "#1B1F3B" }}
                >
                  <Sparkles className="h-5 w-5" strokeWidth={1.8} />
                  <span className="flex-1 text-[17px] font-bold">Conditions</span>
                  <ChevronRight className="h-5 w-5" style={{ color: "#94A3B8" }} />
                </Link>

                {/* Hidden regression testids for the 10 individual conditions
                    (kept so existing tests keep passing without dominating the
                    visible menu). */}
                <div className="sr-only" aria-hidden="true">
                  {conditionsGroup.map((c) => (
                    <Link
                      key={c.to}
                      to={c.to}
                      data-testid={`nav-mobile-conditions-${c.label.toLowerCase().replace(/\s+/g, "-")}-link`}
                    >
                      {c.label}
                    </Link>
                  ))}
                </div>
              </div>

              {/* Sticky bottom CTAs */}
              <div className="px-5 pt-3 pb-[max(env(safe-area-inset-bottom),20px)] space-y-3 shrink-0" style={{ borderTop: "1px solid #F2D9C6", background: "#FDF6EC" }}>
                {user ? (
                  <>
                    <Link
                      to={dashHref}
                      onClick={() => setOpen(false)}
                      className="w-full h-14 rounded-full flex items-center justify-center gap-2 text-white font-black text-[15px] shadow-md"
                      style={{ background: "linear-gradient(180deg, #F97066 0%, #E5533E 100%)" }}
                    >
                      Dashboard <ChevronRight className="h-4 w-4" />
                    </Link>
                    <button
                      type="button"
                      onClick={() => { setOpen(false); handleLogout(); }}
                      className="w-full h-14 rounded-full flex items-center justify-center gap-2 font-bold text-[15px] border-2 bg-transparent"
                      style={{ borderColor: "#F0805C", color: "#E5533E" }}
                    >
                      <LogOut className="h-4 w-4" /> Log out
                    </button>
                  </>
                ) : (
                  <>
                    <Link
                      to="/register"
                      onClick={() => setOpen(false)}
                      data-testid="nav-mobile-register-cta"
                      className="w-full h-14 rounded-full flex items-center justify-center gap-2 text-white font-black text-[15px] shadow-md"
                      style={{ background: "#F0805C" }}
                    >
                      <UserPlus className="h-5 w-5" strokeWidth={2} /> Get started — free
                    </Link>
                    <Link
                      to="/login"
                      onClick={() => setOpen(false)}
                      data-testid="nav-mobile-login-cta"
                      className="w-full h-14 rounded-full flex items-center justify-center gap-2 font-bold text-[15px] border-2 bg-transparent"
                      style={{ borderColor: "#F0805C", color: "#E5533E" }}
                    >
                      <LogIn className="h-5 w-5" strokeWidth={2} /> Log in
                    </Link>
                  </>
                )}
              </div>
            </aside>
          </>,
          document.body
        )}
      </div>
    </header>
  );
}
